import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ImpersonationBanner } from './ImpersonationBanner';
import { Padel24Logo } from '../ui';
import { navigateToSurface } from '../../app/surface';
import { StaffAuthPanel } from '../auth';
import { useStaffAuth } from '../../state/useStaffAuth';
import { fetchMe, type MeResult } from '../../lib/api/me';
import { PlatformUsersSection } from './PlatformUsersSection';
import { EngineSettingsSection } from './EngineSettingsSection';
import { PermissionMatrixSection } from './PermissionMatrixSection';
import { OrganizationsSection } from './OrganizationsSection';
import { RatingCorrectionSection } from './RatingCorrectionSection';
import { PlayerProfileSupportSection } from './PlayerProfileSupportSection';
import { AuditSection } from './AuditSection';
import { ReportsSection } from './ReportsSection';
import { PaymentExceptionsSection } from './PaymentExceptionsSection';
import { useT, type MessageKey } from '../../i18n';
import {
  clubSession as appClubSession,
  signOutCustomerSessions,
  staffAuth as appStaffAuth,
} from '../../lib/auth';
import { selectCustomerSurface } from '../../lib/api/customerContext';
import { setEntraRequiredHandler } from '../../lib/api/entraRequiredSignal';
import { devStaffLoginAvailable } from '../../lib/api/devStaffLogin';
import { DevStaffLoginForm } from './DevStaffLoginForm';
import {
  clearEntraRedirectAttempted,
  entraRedirectAttempted,
  markEntraRedirectAttempted,
  saEntry,
} from './saEntry';
import { saWriteRights } from './saWriteCapabilities';
import type { ClubSession, ClubSessionState, StaffAuth, StaffAuthState } from '../../lib/auth';

// ─────────────────────────────────────────────────────────────────────
// /sa — the Starpoint internal control panel.
//
// WHO THIS IS FOR. Starpoint staff, signing in with Microsoft Entra. NOT club
// administrators and NOT players: they are customers, they are not in
// Starpoint's directory, and they get their own accounts (docs/architecture/SECURITY.md).
//
// Mounted from main.tsx BEFORE Padel24App, like /pad: this surface has its own
// identity model and must not sit behind the club login gate.
//
// SIGNING IN IS NOT A ROLE. Reaching this page with a valid Microsoft account
// means Starpoint employs you; it does not mean you administer the platform.
// Sections are keyed to what the SERVER reports for this account, and an
// account with nothing sees nothing.
//
// THE SHAPE (#401): sign in and you land in the panel — the access fetch
// happens by itself, and the shell renders around its answer. A left menu,
// grouped by what you came to do, so sections can be added without the page
// becoming a stack. What is rendered is a VIEW of the server's answer; every
// section's data comes from an endpoint that enforces the same authority
// again. Hiding a menu entry the API would refuse is a courtesy, not a
// control.
//
// NO PERSONA SWITCHING HERE (#444). The persona bar belongs to the normal
// product surface and to one account (`dev@spoint.no`); `/sa` must never
// offer Centre / Club / Player switching. This panel had a bar that signed
// itself in as a dev «global administrator» — which is the door #444 closed,
// and offering the remaining personas here would only move it.
//
// The one dev-specific entry to this panel is `devadmin@spoint.no` through
// the dev staff sign-in, followed by ORDINARY platform RBAC. Acting inside a
// customer's organization is the audited impersonation context (RBAC-3),
// which is a different mechanism with a reason, an expiry and a record —
// everything a persona switch is not.
//
// The panel still accepts a club session as its identity, because the dev
// sign-in issues one and the transport already prefers it for its token. It
// asks the server what that account may do, exactly as for an Entra sign-in.
// ─────────────────────────────────────────────────────────────────────

// The path predicate MOVED to `app/routePaths.ts`, a module that imports
// nothing. It used to live here, and `app/surface.ts` imported it — while this
// file imports `navigateToSurface` from that same module. The cycle compiled
// and ran, and cost a full page reload on every edit (Vite cannot hot-update
// inside one), which signed the developer out every time because the club
// token is deliberately in memory. Re-exported so existing importers and the
// route's own tests are unaffected; nothing imports this file to get it.
export { SUPERADMIN_ROUTE_PATHS, isSuperadminRoutePath } from '../../app/routePaths';

export type SaSection =
  | 'access'
  | 'roles'
  | 'organizations'
  | 'users'
  | 'ratings'
  | 'playerProfile'
  | 'audit'
  | 'reports'
  | 'paymentExceptions'
  | 'engine';

/**
 * A menu label: the legacy entries are Bokmål literals (this file is on the
 * frozen i18n list), and NEW entries are catalog keys — new text goes through
 * the catalog even in a file that has not been migrated (#310a).
 */
type MenuLabel = string | { key: MessageKey };

/** The menu, grouped. A section renders only when the server's answer admits it. */
const MENU: { group: MenuLabel; items: { key: SaSection; label: MenuLabel }[] }[] = [
  {
    group: 'Tilgang',
    // «MIN KONTO» IS GONE (#754 stage 3). It existed to change a LOCAL
    // password, and there are none left: `/me/password` is removed and the dev
    // staff account is an identity with no credential. An entry whose only
    // possible answer is `no_local_credential` is worse than no entry.
    items: [{ key: 'access', label: 'Min tilgang' }],
  },
  {
    group: 'Rettigheter',
    items: [
      { key: 'roles', label: 'Rettigheter' },
      { key: 'organizations', label: 'Organisasjoner' },
      { key: 'users', label: 'Plattformbrukere' },
    ],
  },
  // THE MANUAL RATING CORRECTION (#793) — platform authority since #197, and
  // until this entry existed the only way to perform it was the bare endpoint.
  {
    group: { key: 'saRating.menuGroup' },
    items: [
      { key: 'ratings', label: { key: 'saRating.menu' } },
      // SUPPORT CORRECTS A PLAYER'S IDENTITY (#796). Offered only to a caller
      // holding `member.profile.support_edit` — see `admits` below.
      { key: 'playerProfile', label: { key: 'saPlayerProfile.menu' } },
    ],
  },
  // THE AUDIT CENTRE (#1041). Read-only, and offered only to a caller holding
  // `platform.audit.read` — see `admits` below.
  {
    group: { key: 'saAudit.menuGroup' },
    items: [{ key: 'audit', label: { key: 'saAudit.menu' } }],
  },
  // THE REPORTING CENTRE (#1043). Read-only, and offered only to a caller
  // holding `platform.reports.read` — see `admits` below.
  {
    group: { key: 'saReports.menuGroup' },
    items: [{ key: 'reports', label: { key: 'saReports.menu' } }],
  },
  // THE PAYMENT-EXCEPTION WORKLIST (#1131). Read-only, and offered only to a
  // caller holding `platform.payment_exceptions.read` — see `admits` below.
  {
    group: { key: 'saPayEx.menuGroup' },
    items: [{ key: 'paymentExceptions', label: { key: 'saPayEx.menu' } }],
  },
  { group: 'Innstillinger', items: [{ key: 'engine', label: 'Motor' }] },
];

export interface SuperadminRouteProps {
  /** Injectable for tests; defaults to the app-wide instance. */
  auth?: StaffAuth;
  /** Injectable for tests. */
  loadMe?: typeof fetchMe;
  /** Injectable for tests; defaults to the app-wide club session. */
  clubSession?: ClubSession;
  /**
   * Whether this deployment offers the dev staff door — `/config`, asked once
   * (#789). Injectable for tests. The same answer is handed to the form, so
   * the landing and the form cannot disagree about whether it exists.
   */
  devLoginAvailable?: typeof devStaffLoginAvailable;
}

export function SuperadminRoute({
  auth,
  loadMe = fetchMe,
  clubSession = appClubSession,
  devLoginAvailable = devStaffLoginAvailable,
}: SuperadminRouteProps) {
  const t = useT();
  const label = (l: MenuLabel) => (typeof l === 'string' ? l : t(l.key));
  const { state } = useStaffAuth(auth);
  const staff = auth ?? appStaffAuth;
  const [club, setClub] = useState<ClubSessionState>(() => clubSession.state());
  useEffect(() => clubSession.subscribe(setClub), [clubSession]);

  // ── THIS SURFACE IS `staff`, AND IT SAYS SO (#839 re-review) ─────
  //
  // `customerContext()` falls back to INFERRING the surface from which
  // sessions are signed in, and nothing was selecting one here. So the moment
  // `/sa` also restored a customer cookie (below), a signed-in `clubSession`
  // made the inference answer `'club'` — and the credential decision sent the
  // CUSTOMER credential while the header still showed the Entra name. The
  // server prefers `Authorization` and never falls back to the cookie, so
  // `/me`, `/platform/*` and any write would have been authenticated and
  // AUDITED as the wrong account. Reproduced directly: with both live, the
  // bearer was `sps_customer_…` rather than the Entra token.
  //
  // Saying `'staff'` fixes both halves at once, because the staff surface is
  // the only one whose credential is the Entra token:
  //
  //   ENTRA ACTIVE  → the Entra token is sent, and it beats any cookie.
  //   NO ENTRA      → it returns null, no `Authorization` is sent, and the
  //                   cookie answers — which is exactly how the dev staff OTP
  //                   sign-in has to keep working.
  //
  // CLEARED ON UNMOUNT, because the selection is module-global: leaving it set
  // would follow the operator back to the portal, where `'staff'` means «send
  // no customer credential» and every request would go out unauthenticated.
  // `null` restores the inference, which is the portal's correct behaviour.
  useEffect(() => {
    selectCustomerSurface('staff');
    return () => selectCustomerSurface(null);
  }, []);

  // ── ASK THE COOKIE, ONCE, ON BOOT (#839 review) ──────────────────
  //
  // `/sa` is mounted OUTSIDE `Padel24App` — a different identity model, so it
  // is deliberately not behind the club login gate — and `Padel24App` is the
  // only place that calls `clubSession.restore()`. The consequence was that an
  // account session on this surface lived exactly as long as the tab: the dev
  // sign-in worked, and the next refresh showed the sign-in form again.
  //
  // Only visible once the cookie was actually kept. Before that fix the
  // browser discarded it cross-origin, so «signed out after a reload» had two
  // independent causes and closing one changed nothing — which is how the
  // first of them was shipped.
  //
  // THE ENTRA PATH ALREADY DOES THIS: `useStaffAuth` calls `auth.restore()` on
  // mount. This is the same promise for the other credential, not a new one.
  //
  // IT GRANTS NOTHING. `restore()` asks `/me` and the server answers who the
  // cookie belongs to; what that account may do is read from the database per
  // request, exactly as for a session adopted in this tab. A club user whose
  // cookie is restored here sees «ingen plattformrolle» — the same thing they
  // see today if they navigate to `/sa` without reloading.
  //
  // ── AND NOTHING IS DECIDED UNTIL BOTH HAVE ANSWERED (#789) ───────
  //
  // The same boot also asks MSAL (`restore()`, which processes a redirect
  // return first — shared with `useStaffAuth`'s call, not a second one) and
  // `/config`. Only when all three have answered does a signed-out visitor
  // get sent anywhere: a configured Entra STARTS `signed-out` before its cache
  // is read, so deciding earlier would bounce a signed-in operator to
  // Microsoft on every hard reload.
  const [boot, setBoot] = useState<{ devDoor: boolean } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void Promise.all([staff.restore(), clubSession.restore(), devLoginAvailable()]).then(
      ([, , devDoor]) => {
        if (!cancelled) setBoot({ devDoor });
      },
      // Bounded: an answer that never comes must not leave «checking» forever.
      () => {
        if (!cancelled) setBoot({ devDoor: false });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [staff, clubSession, devLoginAvailable]);

  const [me, setMe] = useState<MeResult | null>(null);
  const [loading, setLoading] = useState(false);
  // What the operator PICKED, or null for «wherever this account lands».
  // Derived rather than defaulted: a `useState('access')` plus an effect that
  // moves it once the answer arrives renders the diagnostic page first and
  // then jumps, which is exactly the flicker this avoids.
  const [chosen, setChosen] = useState<SaSection | null>(null);
  // ── NO `/config` READ HERE ANY MORE (#754 stage 3) ───────────────
  //
  // This asked whether the deployment offers the local staff sign-in, purely
  // to decide whether to show «Min konto». That section is gone with the
  // password, and `DevStaffLoginForm` asks the same flag for itself — so the
  // question now has exactly one asker, which is where it belongs.

  const staffSignedIn = state.status === 'signed-in';
  const clubSignedIn = club.status === 'signed-in';
  const signedIn = staffSignedIn || clubSignedIn;

  // ── STRAIGHT TO MICROSOFT, ONCE (#789) ───────────────────────────
  //
  // See `saEntry.ts` for the table. `attempted` is read once from the tab's
  // marker and only ever set here: the redirect sets it before leaving, and a
  // visitor who comes back without a session therefore gets the retry page,
  // not a second trip. A sign-in clears the marker for the NEXT visit and
  // leaves this mount's flag alone, so signing out here does not bounce.
  const [attempted, setAttempted] = useState(entraRedirectAttempted);
  const entry = saEntry({
    booted: boot !== null,
    staff: state,
    clubSignedIn,
    devDoor: boot?.devDoor ?? false,
    attempted,
  });
  const redirectToEntra = useCallback(() => {
    markEntraRedirectAttempted();
    setAttempted(true);
    void staff.signInRedirect();
  }, [staff]);
  useEffect(() => {
    if (entry === 'redirect') redirectToEntra();
  }, [entry, redirectToEntra]);
  useEffect(() => {
    if (staffSignedIn) clearEntraRedirectAttempted();
  }, [staffSignedIn]);
  // The two-door landing is the dev deployment's page. Everywhere else a
  // signed-out visitor sees only the neutral entry below, which names no panel.
  const showLanding = signedIn || entry === 'doors';

  // ── WHICH ACCESS READ IS STILL THE CURRENT ONE (#839) ────────────
  //
  // `/me` is read once per identity change, and the two identities restore
  // independently — so two reads can be in flight, and the FIRST one started
  // can be the LAST one to answer. Written straight into state, B's older
  // answer then overwrote A's newer one: the header renders A while the menus
  // and the role-derived controls are B's grants.
  //
  // The server still authorises each request on its own, so this is not a way
  // to do anything; it is the panel telling an operator they hold rights they
  // do not, or hiding ones they do — and attributing either to the wrong name.
  //
  // A generation counter rather than an `AbortController`: the obsolete read
  // is not worth cancelling (it is one GET), and what has to be prevented is
  // its RESULT landing. `useRef` so a re-render cannot reset it.
  const accessGeneration = useRef(0);

  // ── THE SERVER SAID THIS SESSION CANNOT CARRY PLATFORM AUTHORITY (#1030) ──
  //
  // Since #1002 a platform role is honoured only on a request Microsoft Entra
  // proved. A customer-door session (an e-mailed code or a passkey) whose
  // account HOLDS a role still gets `authorization.platformRoles` from `/me`
  // — that is what the account holds — so the menu below would offer every
  // section and every one of them would answer `403 entra_required`: a dead
  // menu. The transport reports the code (`entraRequiredSignal.ts`); this
  // turns it into the sentence and takes the dead sections away.
  //
  // Reset when the identity changes (`checkAccess`), because it is an answer
  // about THAT session. And never shown beside an Entra session: the Entra
  // token is what this surface sends when there is one, so a late refusal of
  // a request made before the sign-in must not tell a Microsoft-signed-in
  // operator to sign in with Microsoft.
  const [entraRequired, setEntraRequired] = useState(false);
  useEffect(() => {
    setEntraRequiredHandler(() => setEntraRequired(true));
    return () => setEntraRequiredHandler(null);
  }, []);
  const doorRefused = entraRequired && !staffSignedIn;

  const checkAccess = useCallback(async () => {
    const generation = ++accessGeneration.current;
    const current = () => accessGeneration.current === generation;
    setLoading(true);
    setMe(null);
    setEntraRequired(false);
    try {
      const result = await loadMe();
      if (current()) setMe(result);
    } catch {
      // BOUNDED FAILURE, ALWAYS. `fetchMe` documents that it never throws and
      // renders every outcome as a state — but `loadMe` is injectable, and a
      // rejection here would leave `me` null forever: the panel would sit on
      // «waiting for an answer» with no error and no way to tell the two
      // apart, with the rejection unhandled because the effect calls this with
      // `void`. An error state is what makes the retry button meaningful.
      if (current()) setMe({ state: 'error', status: null });
    } finally {
      // NOT GUARDED, deliberately: `setLoading(true)` above belongs to the
      // newest read, and only the newest read may turn it off. An obsolete
      // completion that cleared it would leave the panel claiming an answer
      // had arrived while the real one is still outstanding.
      if (current()) setLoading(false);
    }
  }, [loadMe]);

  // STRAIGHT IN (#401). The fetch happens on sign-in, not on a button; the
  // button survives as an explicit retry, because the fetch can hang (#400).
  //
  // ── ON EITHER IDENTITY CHANGING, NOT ON «SIGNED IN» (#839) ───────
  //
  // The dependency was the OR of the two, which cannot see one replacing the
  // other. Both restore asynchronously on mount and either can win: if the
  // cookie lands first, `/me` is read as the customer account; when the Entra
  // restore then completes, the OR stays true, nothing re-reads, and the
  // header shows A's name beside B's answer about what may be reached. That is
  // the same «one identity's name, another's authority» confusion the
  // credential fix addresses at the transport, arriving by a different route.
  useEffect(() => {
    if (staffSignedIn || clubSignedIn) void checkAccess();
    else setMe(null);
  }, [staffSignedIn, clubSignedIn, checkAccess]);

  const authorization = me?.state === 'ok' ? me.me.authorization : null;
  const hasPlatformRole = (authorization?.platformRoles.length ?? 0) > 0;
  // WHAT TO OFFER, BY CAPABILITY (#924). Each control is keyed on the
  // capability its own route asks for (`saWriteCapabilities.ts`), from the
  // list the server reported. It used to be one role NAME for every section,
  // `platformRoles.includes('PLATFORM_SUPERADMIN')`, so a matrix edit moving a
  // capability to another role changed what the server allowed and not what
  // this page offered.
  const can = saWriteRights(me?.state === 'ok' ? me.me.platformCapabilities : undefined);
  // WHERE YOU LAND (#428 review). «Min tilgang» is a diagnostic — it answers
  // «what am I allowed to do», which is a question you ask when something is
  // wrong, not the first thing you came to do. An administrator came to
  // administer an organization, so that is the page. An account the server has
  // given no platform role still lands on the diagnostic, because for them it
  // is the only page with an answer on it.
  //
  // A session the server refused at the platform door lands — and stays — on
  // the diagnostic, which is where the sentence and the way out are (#1030).
  const section: SaSection = doorRefused
    ? 'access'
    : (chosen ?? (hasPlatformRole ? 'organizations' : 'access'));

  // THE MENU THE SERVER ADMITS. Built here rather than by hiding the button
  // in the markup, so the same list decides what renders AND what a section
  // key may legitimately be — a hidden-but-reachable section is a section.
  //
  // PER-SECTION, NOT PER-PANEL, and that distinction is the fix for a real
  // contradiction: «Min konto» is identity-derived — it belongs to whoever is
  // signed in, platform role or not — but the whole navigation used to be
  // wrapped in `hasPlatformRole`, so precisely that account could never reach
  // it. The section was correct and unreachable.
  //
  //   access    signed in. It is the account's own diagnostic.
  //   the rest  platform administration, and still gated on the server
  //             having granted a platform role.
  //
  // `account` was the third case and the reason this distinction was drawn:
  // an identity-derived section that `hasPlatformRole` wrongly hid. It is gone
  // with the password (#754 stage 3) and the SHAPE is kept, because the next
  // identity-derived section will need it again — «which language do I read my
  // own screen in» is already one on the customer side.
  const admits = (key: SaSection): boolean => {
    if (!signedIn) return false;
    if (key === 'access') return true;
    // NOT on a session the platform door refused (#1030): every one of these
    // sections would answer `entra_required`.
    if (!hasPlatformRole || doorRefused) return false;
    // THE SECTIONS WITH A CAPABILITY OF THEIR OWN (#1041, #1043). Every other
    // section READS behind any platform role and gates only its writes; the
    // audit trail is not readable on a platform role alone, so its entry is
    // offered only when the server says the caller holds the right its route
    // asks for.
    if (key === 'audit') return can.auditRead;
    // The same for the platform's figures (#1043): its own capability.
    if (key === 'reports') return can.reportsRead;
    // And for the payment-exception worklist (#1131).
    if (key === 'paymentExceptions') return can.paymentExceptionsRead;
    // The support edit (#796): the section IS the edit, so it is offered on
    // the capability its one route asks for.
    if (key === 'playerProfile') return can.playerSupportEdit;
    return true;
  };
  const visibleMenu = MENU.map((g) => ({
    ...g,
    items: g.items.filter((i) => admits(i.key)),
  })).filter((g) => g.items.length > 0);

  const whoami = staffSignedIn
    ? (state.identity.displayName ?? state.identity.email ?? 'Innlogget')
    : (club.account?.displayName ?? club.account?.email ?? 'Innlogget');

  return (
    <div className="min-h-screen bg-bg text-ink font-body">
      {/* ABOVE EVERYTHING, and sticky (RBAC-3). The expensive mistake is not
          starting a context — it is forgetting you are in one. */}
      <ImpersonationBanner />
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Padel24Logo size="sm" variant="full" />
          {showLanding && (
            <span className="font-mono text-[11px] uppercase tracking-wider text-ink-3">
              Superadmin
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            {signedIn && (
              <span className="text-xs text-ink-3" data-testid="sa-whoami">
                {whoami}
              </span>
            )}
            {clubSignedIn && !staffSignedIn && (
              <button
                type="button"
                className="text-[11px] text-ink-3 underline"
                onClick={() => void signOutCustomerSessions()}
              >
                Logg ut
              </button>
            )}
            {/* A TRANSITION, not a page load (RBAC-3). The `href` stays, so
                middle-click, «open in new tab» and screen readers all still
                see a link to the portal — but an ordinary click is handled
                here, because a reload destroys an active organization context
                and would drop an operator into the club login screen. */}
            <a
              href="/"
              className="text-[11px] text-ink-3 underline"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigateToSurface('portal');
              }}
            >
              Klubbportalen
            </a>
          </div>
        </div>
      </header>

      <div
        className={`max-w-6xl mx-auto px-4 py-8 ${
          visibleMenu.length > 0 ? 'md:grid md:grid-cols-[13rem_1fr] md:gap-8' : ''
        }`}
      >
        {/* NOT RENDERED UNTIL THE SERVER HAS ADMITTED SOMETHING (#428 review).
            It used to render always, with the entries merely `disabled` — so
            the sign-in page advertised «Rettigheter · Organisasjoner ·
            Plattformbrukere» to anyone who opened /sa, including people with
            no account at all. A greyed-out menu is still a menu: it tells a
            stranger what this panel contains and tells a signed-in employee
            with no role that there is something here being withheld. Neither
            is a thing to say before the server has answered.

            THAT PROPERTY IS UNCHANGED, but the condition is no longer
            `hasPlatformRole`. `admits()` returns false for every section
            when nobody is signed in, so `visibleMenu` is empty and this still
            renders nothing to a stranger. What changed is the case in
            between: a signed-in account with NO platform role now gets a
            menu containing its own two sections and nothing else, because
            «you have no role» and «you may not reach your own account» are
            different statements and the panel was making the first into the
            second. */}
        {visibleMenu.length > 0 && (
          <nav className="mb-6 md:mb-0" data-testid="sa-menu" aria-label="Seksjoner">
            {visibleMenu.map((g) => (
              <div key={label(g.group)} className="mb-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                  {label(g.group)}
                </div>
                <ul className="space-y-0.5">
                  {g.items.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => setChosen(item.key)}
                        className={`w-full text-left rounded px-2 py-1.5 text-xs ${
                          section === item.key ? 'bg-white/10 font-medium' : 'hover:bg-white/5'
                        }`}
                        aria-current={section === item.key ? 'page' : undefined}
                        data-testid={`sa-menu-${item.key}`}
                      >
                        {label(item.label)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}

        {/* `min-w-0`, BECAUSE THIS IS A GRID ITEM (#861). A grid item's minimum
            width defaults to its content's, so the twelve-column centre board
            in «Rettigheter» widened the `1fr` track to ~1900 px: the panel ran
            out of the `max-w-6xl` page and the whole page scrolled sideways,
            while the board's own sideways scroll had nothing to do. With the
            track held to the page, the board scrolls inside its card. */}
        <main className="min-w-0">
          {!showLanding && (
            <SignedOutEntry
              entry={entry}
              staff={state}
              onRetry={redirectToEntra}
              misconfiguredPanel={
                state.status === 'misconfigured' ? <StaffAuthPanel auth={auth} /> : null
              }
            />
          )}
          {showLanding && section === 'access' && (
            <div className="card p-6" data-testid="sa-panel">
              <h1 className="font-display text-2xl font-semibold text-ink">
                Internt kontrollpanel
              </h1>
              <p className="text-sm text-ink-3 mt-1">
                Kun for ansatte i Starpoint. Klubbadministratorer og spillere logger inn i
                klubbportalen, ikke her.
              </p>
              {/* TWO DOORS SIDE BY SIDE where both exist: the Microsoft one for
                  staff, the developer one on dev. Stacked, the second read as
                  an afterthought under the first — and on dev it is the one
                  that actually works. */}
              <div className="mt-6 grid gap-4 md:grid-cols-2 items-stretch">
                {/* Renders nothing when Entra is unconfigured, so this page degrades
                    to an honest "not available here" rather than a dead button. */}
                {/* GATED ON THE ENTRA SESSION, NOT ON A CUSTOMER ONE
                    (#839 re-review). It was `!clubSignedIn`, which was
                    harmless while nothing restored a club session here — and
                    became «a customer cookie hides the Microsoft sign-in» the
                    moment one did. A StarPoint employee who also holds a
                    customer session would have had no way in short of signing
                    that session out. The Microsoft door is about the Entra
                    identity, so only an Entra session closes it. */}
                {!staffSignedIn && <StaffAuthPanel auth={auth} />}
                {/* Dev only, and the DEPLOYMENT decides: renders nothing
                    unless /config says the endpoint is mounted (#445). */}
                {!signedIn && !clubSignedIn && <DevStaffLoginForm available={devLoginAvailable} />}
              </div>
              {!signedIn && state.status !== 'misconfigured' && (
                <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-signed-out">
                  Logg inn med Microsoft for å fortsette.
                </p>
              )}
              {signedIn && doorRefused && (
                <EntraRequiredNotice
                  canSignIn={state.status !== 'disabled' && state.status !== 'misconfigured'}
                  onSignIn={redirectToEntra}
                />
              )}
              {signedIn && (
                <div className="mt-5 border-t border-white/10 pt-4">
                  <h2 className="font-display text-sm font-semibold">Tilgang</h2>
                  <p className="text-[11px] text-ink-3 mt-1">
                    Innlogging bekrefter hvem du er. Hva du har tilgang til bestemmes av Starpoint,
                    og hentes fra serveren.
                  </p>
                  <button
                    type="button"
                    className="btn-ghost text-xs mt-3"
                    disabled={loading}
                    onClick={() => void checkAccess()}
                    data-testid="sa-check-access"
                  >
                    {loading ? 'Henter…' : 'Hent tilgang på nytt'}
                  </button>
                  {me && <AccessReport result={me} doorRefused={doorRefused} />}
                </div>
              )}
            </div>
          )}

          {/* Sections render only once the SERVER has said the account holds a
              platform role. Signing in is not enough, and the page never decides
              this for itself — `me` is the server's answer. */}
          {signedIn && hasPlatformRole && section === 'roles' && (
            <PermissionMatrixSection canWrite={can.matrix} />
          )}
          {signedIn && hasPlatformRole && section === 'organizations' && (
            <OrganizationsSection
              canWrite={can.organizationSetup}
              canGrant={can.organizationRoles}
              canImpersonate={can.impersonation}
            />
          )}
          {signedIn && hasPlatformRole && section === 'users' && (
            <PlatformUsersSection canWrite={can.userRoles} canManageAccounts={can.userAccounts} />
          )}
          {/* READ for every platform role, CORRECT only for a caller holding
              `rating.adjust_manual`, the capability the route asks for at
              platform scope. A matrix edit granting it to support is now seen
              here (#924). */}
          {signedIn && hasPlatformRole && section === 'ratings' && (
            <RatingCorrectionSection canWrite={can.ratingCorrection} />
          )}
          {signedIn && hasPlatformRole && can.playerSupportEdit && section === 'playerProfile' && (
            <PlayerProfileSupportSection />
          )}
          {signedIn && hasPlatformRole && can.auditRead && section === 'audit' && <AuditSection />}
          {signedIn && hasPlatformRole && can.reportsRead && section === 'reports' && (
            <ReportsSection />
          )}
          {signedIn &&
            hasPlatformRole &&
            can.paymentExceptionsRead &&
            section === 'paymentExceptions' && <PaymentExceptionsSection />}
          {signedIn && hasPlatformRole && section === 'engine' && (
            <EngineSettingsSection canWrite={can.engineSettings} />
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * The neutral page a signed-out visitor sees where there is no dev door (#789).
 *
 * It names no panel and lists nothing. The retry is a click, so it is the
 * person's decision to go back to Microsoft — after a cancel or an error, an
 * automatic one is the loop the issue forbids.
 */
function SignedOutEntry({
  entry,
  staff,
  onRetry,
  misconfiguredPanel,
}: {
  entry: ReturnType<typeof saEntry>;
  staff: StaffAuthState;
  onRetry: () => void;
  misconfiguredPanel: ReactNode;
}) {
  const t = useT();
  if (entry === 'retry') {
    return (
      <div className="card p-6 max-w-md mx-auto" data-testid="sa-entry-retry">
        <h1 className="font-display text-xl font-semibold text-ink">{t('saEntry.title')}</h1>
        <p className="text-sm text-ink-3 mt-2" data-testid="sa-entry-message">
          {/* describeAuthFailure's text is already safe to render (no token,
              no response detail) — the reason Entra gave, in plain words. */}
          {staff.status === 'error' ? staff.message : t('saEntry.notCompleted')}
        </p>
        <button
          type="button"
          className="btn-primary text-sm mt-4"
          onClick={onRetry}
          data-testid="sa-entry-signin"
        >
          {t('saEntry.signIn')}
        </button>
      </div>
    );
  }
  if (entry === 'unavailable') {
    return (
      <div className="card p-6 max-w-md mx-auto" data-testid="sa-entry-unavailable">
        <h1 className="font-display text-xl font-semibold text-ink">{t('saEntry.title')}</h1>
        <p className="text-sm text-ink-3 mt-2">{t('saEntry.unavailable')}</p>
        {misconfiguredPanel}
      </div>
    );
  }
  // checking | redirect | redirecting: the page is about to leave or is still
  // asking. One quiet line — not the landing, and not a flash of it.
  return (
    <p className="text-sm text-ink-3 text-center mt-8" role="status" data-testid="sa-entry-wait">
      {entry === 'checking' ? t('saEntry.checking') : t('saEntry.redirecting')}
    </p>
  );
}

/**
 * The panel areas each platform role opens, for display only.
 *
 * The server's guards are what actually admit or refuse a request. Listed so
 * a reader can see that access is keyed to a ROLE rather than to having
 * signed in, and so the gap between "employed" and "entitled" is visible.
 */
const PLATFORM_ROLE_AREAS: Record<string, string> = {
  PLATFORM_SUPERADMIN: 'alle områder',
  PLATFORM_OPERATIONS: 'klubber og tillegg',
  PLATFORM_SUPPORT: 'brukersøk og feilsøking',
  PLATFORM_FINANCE: 'rapporter og fakturering',
  PLATFORM_READONLY: 'lesetilgang',
};

/**
 * The sentence for `403 entra_required` (#1030), and the way out.
 *
 * The CODE is the server's; this is the page's reading of it. The button is
 * the same Microsoft sign-in the signed-out entry uses. Where this deployment
 * has no Microsoft sign-in at all, it says so instead of offering a button
 * that cannot work.
 */
function EntraRequiredNotice({
  canSignIn,
  onSignIn,
}: {
  canSignIn: boolean;
  onSignIn: () => void;
}) {
  const t = useT();
  return (
    <div
      className="mt-5 rounded-xl border border-line bg-paper p-4"
      role="alert"
      data-testid="sa-entra-required"
    >
      <h2 className="font-display text-sm font-semibold text-ink">{t('saEntraRequired.title')}</h2>
      <p className="text-xs text-ink-2 mt-1">{t('saEntraRequired.body')}</p>
      {canSignIn ? (
        <button
          type="button"
          className="btn-primary text-xs mt-3"
          onClick={onSignIn}
          data-testid="sa-entra-required-signin"
        >
          {t('saEntraRequired.signIn')}
        </button>
      ) : (
        <p className="text-[11px] text-ink-3 mt-2">{t('saEntraRequired.unavailable')}</p>
      )}
    </div>
  );
}

/** Renders whatever the server said — including "you have nothing". */
function AccessReport({
  result,
  doorRefused = false,
}: {
  result: MeResult;
  /** The server refused this session at the platform door (#1030). */
  doorRefused?: boolean;
}) {
  if (result.state === 'no-api') {
    return (
      <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-access">
        API-et er ikke koblet til i denne versjonen ennå.
      </p>
    );
  }
  if (result.state === 'unauthorized') {
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-access">
        Serveren godtok ikke innloggingen. Logg inn på nytt.
      </p>
    );
  }
  if (result.state === 'error') {
    // TWO DIFFERENT PROBLEMS, AND THE REMEDY IS NOT THE SAME (#400). A status
    // means the server answered and refused; no status means it never
    // answered at all — which here is almost always a cold start, because
    // every deploy restarts the App Service and the first request afterwards
    // pays for it. «Try again» is the honest instruction for that one, and
    // the retry succeeds immediately once the container is warm.
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-access">
        {result.status
          ? `Kunne ikke hente tilgang (HTTP ${result.status}).`
          : 'Serveren svarte ikke. Prøv igjen om et øyeblikk – den kan ha startet opp.'}
      </p>
    );
  }

  const { platformRoles, tenants } = result.me.authorization;

  // Keyed to the ROLE, not to being signed in. No platform role means no
  // section, however valid the Microsoft account is.
  if (platformRoles.length === 0) {
    return (
      <div className="mt-3 text-[11px] text-ink-3" data-testid="sa-access">
        <p data-testid="sa-no-role">
          Kontoen er bekreftet, men har ingen plattformrolle. Ingen områder er tilgjengelige. Roller
          tildeles av Starpoint.
        </p>
        {tenants.length > 0 && (
          <p className="mt-1">
            Du har tilgang til {tenants.length} organisasjon(er) i klubbportalen, men det gir ingen
            tilgang her.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 text-[11px] text-ink-3" data-testid="sa-access">
      <ul className="space-y-1" data-testid="sa-roles">
        {[...platformRoles].sort().map((role) => (
          <li key={role}>
            <span className="font-mono">{role}</span>
            {PLATFORM_ROLE_AREAS[role] ? ` — ${PLATFORM_ROLE_AREAS[role]}` : ''}
          </li>
        ))}
      </ul>
      {/* Not on a refused session: there is no area in the menu to choose,
          and the notice above says why (#1030). */}
      {!doorRefused && (
        <p className="mt-2">
          Velg et område i menyen. Tilgang håndheves uansett på serveren, ikke her.
        </p>
      )}
    </div>
  );
}
